/**
 * The bay Rosalie is born in — spec §7 beat 1, *Born*.
 *
 * The whole beat happens inside one farrowing bay, roughly 2.4 m by 2.9 m, and
 * she never leaves it. That is not a scope compromise; it is the staging. She is
 * three days old, the room is the only world she has, and beat 2 (*The Weakest
 * Latch*) is the first time she gets out of it. A beat 1 that ranged over the
 * whole hall would spend the one thing the beat has — that this small cage is
 * everything — before it had said it.
 *
 * ## What is in here, and what each thing is for
 *
 * | Object | Why it exists |
 * |---|---|
 * | her mother, in the crate | the argument (see `sow.ts`) |
 * | the crate rails | the first thing in the game that refuses her |
 * | two littermates in the straw | so that one of them can stop being there |
 * | the straw bed | the only soft thing in the building, and the `root` verb |
 * | the heat lamp | the only warmth, and the only warm LIGHT in the zone |
 * | the walkway door | the door the man comes through, and goes back through with her littermate |
 *
 * ## The one number everything is placed against
 *
 * `BAY_Z`. The near-end bay, the one closest to the door, so the man has the
 * shortest possible walk and the player can see the door from where she lies.
 * Everything else in this file is an offset from it.
 *
 * ## Collision, and why this room needed some
 *
 * Until now the pens had no colliders at their slat decks and it did not matter,
 * because the 0.55 m kerb refused her and nothing was reachable (STATUS's
 * carried-over task 5). Beat 1 puts her *inside* a pen, so the deck, the crate's
 * lane rails and the creep kerb all have to be real. **The rails especially**:
 * the beat's first sentence is "the bars are the first thing the player
 * understands", and a bar she walks through understands nothing.
 *
 * Colliders come from `bornBaySolids()`, the same list the meshes are placed
 * from, for the reason `farrowing.ts` and `testroom.ts` both give: a room whose
 * collision does not match what you can see is a source of phantom bugs, and
 * "she clips the rail" becomes indistinguishable from a controller defect.
 */

import * as THREE from 'three';
import { getZone } from '@/render/palette';
import { disposeAsset, surface } from '@/models/materials';
import { sleepingPiglet } from '@/models/piglet';
import { buildPlush, findRosalieParts } from '@/models/rosalie';
import { SIBLING } from '@/render/palette';
import { sowAsset, findSowParts, type SowParts } from '@/models/sow';
import { strawClump } from '@/models/env/straw-clump';
import { farrowingGateAccessRailEnd } from '@/models/env/farrowing-crate';
import { getAsset } from '@/models/registry';
import type { Fixture } from '@/render/lighting';
import { ROSALIE_TUNING } from '@/physics/character';
import { BAY_PITCH, FARROWING_ZONE_ID, HALL, WALKWAY_DOOR, farrowingSolids, penGateMouths } from '@/world/farrowing';
import { collapse, collapseRigged, instanceGroup } from '@/world/instancing';
import { soleSolidWithRole, type SolidRole } from '@/world/solidRoles';
import type { SiblingBounds } from '@/world/bornSibling';

/**
 * Z centre of her bay: the first one at the near end.
 *
 * Matches `bayCentres()` in `farrowing.ts` — the crates are already instanced on
 * that grid and hers is one of them, not a second crate placed on top. Restating
 * the arithmetic rather than exporting it from there is deliberate: it is one
 * line, and importing a private helper to share it would make the hall's layout
 * function part of this beat's interface.
 */
export const BAY_Z = -HALL.halfLength + BAY_PITCH / 2;

/** Which side of the walkway. Negative X, so the door is over her right shoulder. */
const SIDE = -1;

/** Slat deck height, as `farrowing.ts` uses. Anything in a pen sits on this. */
const SLAT_HEIGHT = 0.09;

/** Where the pen floor starts, outboard of the kerb. */
const PEN_INNER = HALL.walkway + 0.12;

/** The crate's own centre in X, from the hall's dressing. */
const CRATE_X = SIDE * (PEN_INNER + 1.15);

/**
 * The crate is laid a QUARTER TURN, and that is the fact everything here is
 * placed against.
 *
 * `dressFarrowingHall` rotates each crate by ±90° so its 2.1 m length runs *into*
 * the pen rather than along the hall — which means the crate's own local X, where
 * its two creep bays sit at ±0.61, comes out along **world Z**. The creep areas
 * are therefore in front of and behind the sow lane as seen from the walkway, not
 * beside it.
 *
 * The first version of this file placed the whole beat as though the creep were
 * offset in X. Every position was then 0.61 m from where it read on the page, and
 * the symptom was not subtle: the opening shot rendered the inside face of a
 * creep-bay kerb, full frame, because Rosalie had been spawned in the 0.31 m slot
 * between the crate and the pen kerb — a gap narrower than she is. It was found
 * by taking the screenshot, which is the only way it could have been.
 */

/** The creep strip she lives in: 2.1 m along X, 0.54 m deep in Z. */
export const CREEP_Z = BAY_Z - 0.61;

/**
 * The near creep, not the far one.
 *
 * Both bays are identical; this is the one on the door side of her mother, so the
 * door and the man are in shot from where she lies. In a beat whose whole
 * event happens at a doorway, that is not a small thing.
 */
export const CREEP_X = CRATE_X;


/** Deck top in world Y. Everything in the bay stands on this, including her. */
export const DECK_Y = SLAT_HEIGHT;

/**
 * The kerb's inner face, and the line every bay solid has to actually REACH.
 *
 * ── R-023, and it is the softlock Alex reported three sessions running ────────
 *
 * The crate's collider slabs were written as `half.x = 1.05` about `CRATE_X`,
 * which spans x = −3.02 to **−0.92**. The kerb's inner face is at −0.82. So each
 * slab stopped **0.10 m short of the kerb**, and a 0.10 m slot is narrower than
 * her 0.135 m capsule radius: pressing into the end cap of a slab wedges the
 * capsule between two solids that are closer together than it is, and the solver
 * resolves that the only way it can — it squeezes her **up**, onto the 0.55 m
 * kerb top, from where she simply walks off into the walkway.
 *
 * Measured rather than reasoned: pressing the bay boundary at 24 headings from
 * six start points produced 16 escapes, every one of them peaking at y = 0.550
 * (the kerb top exactly) and popping at x = −0.86…−0.92 — inside the slot. The
 * only bay solids that already reached −0.82 are the two bay dividers, and their
 * z rows are precisely the rows that produced **no** pop. That is the whole
 * diagnosis in one number.
 *
 * Why it was a softlock and not a shortcut: the kerb is 0.55 m of concrete and
 * she is 0.32 m with a 0.34 m scramble, so from the walkway there is no way back
 * in — measured at nine walkway points beside the bay, `peakY` never left 0.000.
 * Getting out is one-way, and beat 1 lives entirely inside the pen.
 *
 * **So: derive the inner end from the kerb, never from a half-extent.** A slab
 * whose reach is written as its own width is a slab that silently stops short
 * again the next time the crate moves.
 */
const KERB_INNER_X = SIDE * PEN_INNER;

/**
 * How far every bay run is pushed INTO the kerb rather than stopped against it.
 *
 * Closing the 0.10 m slot took the escapes from 16 to 4, and the four survivors
 * were all in one corner — where an extended run now ended *exactly* on the
 * kerb's inner face. Two boxes that share a face share a zero-width seam, and
 * the solver can find a separating direction along it, so a capsule pressed into
 * the corner still climbed out at y = 0.550. Meeting is not the same as sealing.
 *
 * `farrowing.ts` already learned this once — its floor slabs "stopped 20 mm
 * short of meeting" and had to be widened. This is the same lesson from the
 * other side: abutting is a gap you cannot see in the numbers.
 *
 * 0.06 m lands the runs on the kerb's mid-plane. All of it is buried inside
 * 0.12 m of concrete, so nothing is reachable from either face.
 */
const KERB_BITE = 0.06;

/** The X every bay run ends at: inside the kerb, not against it. */
const KERB_SEAL_X = KERB_INNER_X - SIDE * KERB_BITE;

/** Z offsets from `BAY_Z` of the lane partitions and the outer creep kerbs, and
 *  their half-thickness. Named because `bornAllowedRegions` reads the same lines. */
const LANE_PARTITION_DZ = 0.325;
const CREEP_KERB_DZ = 0.9;
const BAY_RAIL_HALF = 0.03;

/**
 * Turn a pair of world-X limits into the cuboid Rapier wants.
 *
 * The point is that the call sites below read as "from here to the kerb" rather
 * than as two numbers that have to be kept agreeing with each other by hand.
 */
function spanX(fromX: number, toX: number): { halfX: number; centreX: number } {
  return { halfX: Math.abs(toX - fromX) / 2, centreX: (fromX + toX) / 2 };
}

/**
 * Where she wakes: in the straw, alongside her mother, under the lamp — **and
 * which way she is pointing**, which had never been authored and was wrong.
 *
 * `CharacterController` spawns at `facing = 0`, i.e. world +Z. The creep strip
 * is 2.1 m along X and **0.54 m deep in Z**, so a piglet facing +Z in it is
 * facing across a gap narrower than she is long. Nothing enforced that and
 * nothing looked: the opening shot is authored as a close-up of her face, and it
 * had therefore always been standing at her flank. R-024's "it does not read as
 * a piglet" is that, and no camera offset could have fixed it — measured over
 * four facings × five framings in `opening-camera-sweep.spec.ts`, where the
 * shipped shot scores 0.07 frontality (pure profile) and this heading scores
 * 0.97.
 *
 * **−0.60 rad, and the exact value is a constraint satisfied rather than a
 * preference.** The picture wanted −0.97: along the creep toward the crate's far
 * end, which is the only direction with room to stand a lens back in, and the
 * side the heat lamp is on. The golden trace then reported `interaction.actId`
 * null for all 1800 ticks — her mother's interactable sits at bearing ~0 and
 * `nudgeConeHalfAngle` is 0.7, so pointing her at the camera had switched off
 * the beat's first tutorial verb. **D-043 exists because that tutorial order
 * was once enforced by nothing at all**; losing it to a camera change would be
 * the same mistake in a different hat.
 *
 * −0.60 is inside the cone with 0.1 rad to spare and still scores 0.82
 * frontality against the opening lens — a three-quarter rather than a flank, and
 * a face turned dead-on to a lens is the flattest portrait there is anyway.
 * Both numbers are measured, in `opening-camera-sweep.spec.ts`.
 *
 * If this value moves, **two things have to be re-checked, not one**: the head
 * offset `REVEAL_FROM` aims at, and whether the mother is still in the nudge
 * cone. The trace catches the second one on its own.
 */
export const BORN_SPAWN = {
  x: CRATE_X + 0.42, y: DECK_Y + 0.06, z: CREEP_Z, facing: -0.6,
} as const;

/** Where the sibling lies — and then does not. */
export const SIBLING_TAKEN_AT = { x: CRATE_X - 0.3, y: DECK_Y, z: CREEP_Z + 0.04 } as const;

/**
 * Which way the taken sibling faces where it sleeps: the root yaw its sleeping
 * mesh is placed at. When it lies back down it turns to this, so the pose it
 * gets up from and the one it returns to are the same shape.
 */
const SIBLING_TAKEN_YAW = 0.6;

/**
 * Where the taken sibling may go once it is awake (D-250, `bornSibling.ts`).
 *
 * From its own bed toward the pen gate, and **no nearer the gate than
 * `CRATE_X + 0.35`**: the gate mouth is at this strip's walkway end, and a
 * standing piglet near it would be the step onto the gate `bay-staging.spec.ts`
 * exists to forbid. The spec measures `awakeSiblingSolid` at this limit.
 *
 * The lamp and the near battens light that whole range, and both go down
 * for the taking, so wherever it lies down, the taking is in the dark.
 */
export const SIBLING_BOUNDS: SiblingBounds = {
  bed: { x: SIBLING_TAKEN_AT.x, z: SIBLING_TAKEN_AT.z, yaw: SIBLING_TAKEN_YAW },
  xFar: CRATE_X + 0.35,
  zCentre: CREEP_Z,
  zWander: 0.05,
};

/**
 * How far the awake sibling's body centre sits above its feet: the roll
 * pivot it gets up and lies down about (D-250, `BornDirector`). Her 0.32 m
 * height less the legs' share; the plush hangs this far below the pivot.
 */
export const AWAKE_SIBLING_CENTRE_Y = 0.17;

/**
 * The awake sibling's collider at a point, as the part moves it (D-250).
 *
 * **0.19 m tall, lower than the piglet it stands for, on purpose.** Rosalie
 * autosteps ~0.16 m and scrambles from 0.22 m (`character.ts`); a box in the
 * dead band between them is one she cannot climb. A collider the height of a
 * standing piglet (0.32 m) would be a scramble — onto its back, and from there
 * toward the gate. Long along X because it only ever travels along the strip.
 */
export function awakeSiblingSolid(at: { x: number; z: number }): BaySolid {
  return {
    name: 'sibling-awake',
    half: { x: 0.3, y: 0.095, z: 0.12 },
    pos: { x: at.x, y: DECK_Y + 0.095, z: at.z },
    visible: false,
    colour: 0x000000,
    kind: 'organic',
  };
}

/**
 * The other sibling, who stays — **and who used to be a staircase out of the
 * beat**.
 *
 * ── The escape `pen-containment.spec.ts` was red on, and what it actually was ─
 *
 * She lay at `CRATE_X + 0.92`, in the pocket between Rosalie's spawn and the
 * kerb. Her collider is measured from the sleeping mesh, so it is **0.41 m tall
 * and reaches x = −0.78** — 0.04 m past the kerb's inner face. On its own that
 * is harmless, and for six sessions it was: the kerb top is 0.55, and 0.55 −
 * 0.41 = **0.14 m falls in the dead band** between what autostep really climbs
 * (~0.16, see `character.ts`) and `scrambleMinHeight` (0.22). She gets her chin
 * on the kerb and is refused, which is what the sweep's `peakY=0.550 maxX=−0.830`
 * rows are.
 *
 * **The kerb is not continuous.** `penGateMouths()` declares a mouth at exactly
 * `BAY_Z`, so `farrowingSolids()` builds the kerb up to z = −9.3 and again from
 * z = −8.3, and what stands between is the gate leaf — **0.62 m**, seven
 * centimetres proud of the kerb. Her collider's z span reached −9.136 and the
 * mouth begins at −9.3, so for **0.164 m of overlap** there was a 0.41 m
 * platform welded to a 0.62 m gate. 0.62 − 0.41 = 0.21, and from her sliding off
 * its edge at 0.34 it is 0.28 — both inside the 0.22…0.34 scramble band. She
 * scrambles onto the gate and steps off into the walkway, where beat 1 cannot be
 * finished.
 *
 * Traced tick by tick rather than inferred: `gait=scramble` from y = 0.342 to
 * y = 0.620 over eighteen ticks, then `walk` along the gate top, then the
 * walkway. **Every failing run since session 33 reported `peakY=0.620` and it was
 * read as the kerb top plus something. It is the gate, exactly.**
 *
 * ── The rule, and why it is not "away from the kerb" ─────────────────────────
 *
 * Nothing standable higher than **`gate top − scrambleMaxHeight`** may sit within
 * scramble reach of a mouth. Against the kerb the same littermate is safe; it is
 * the extra 0.07 m of gate that turns refused into escaped, so a rule written
 * about the kerb would have passed this staging and did.
 *
 * So she moves to the far end of the creep, beside her sibling rather than
 * across Rosalie from her. Three things get better and none get worse: the
 * littermates read as a pile, which is how piglets sleep; the gap left when one
 * of them is lifted is a gap **in that pile**, a metre from where the last stage
 * already walks Rosalie's snout (`SIBLING_TAKEN_AT` is the `empty` interactable);
 * and nothing stands between her and the shadow that comes over the kerb.
 *
 * `bay-staging.spec.ts` asserts the rule over every solid in the bay, so the next
 * thing staged in that pocket fails at once instead of six sessions later.
 */
const SIBLING_KEPT_AT = { x: CRATE_X - 0.86, y: DECK_Y, z: CREEP_Z - 0.05 } as const;

/** The lamp, and therefore the one warm thing in the frame. */
export const LAMP_AT = { x: CRATE_X + 0.1, y: 1.02, z: CREEP_Z } as const;

/**
 * Her mother, in the lane, **head toward the walkway**.
 *
 * Which way round she lies is a real decision and not a default. The feed trough
 * is at the walkway end of every crate (`dressFarrowingHall` places it there,
 * because that is where a person can reach it without going in), so a sow is
 * penned nose-to-the-passage. It also means her head is the part of her Rosalie
 * can get nearest to, and her head is where the bars are densest.
 */
export const SOW_AT = { x: CRATE_X, y: DECK_Y + 0.035, z: BAY_Z } as const;

/** Which way she faces: local +Z onto world +X, i.e. nose toward the walkway. */
const SOW_FACING = Math.PI / 2;

/**
 * How far `BornDirector` ever lifts her head, radians about the head pivot's X
 * (negative is up). Here, not in the director, because her head collider is
 * sized to cover this pose as well as the resting one: a collider measured at
 * rest alone is a head that moves out of its own collision.
 */
export const SOW_HEAD_LIFT_MAX = -0.336;

/**
 * Where beat 1's three tutorial verbs are done: the interactables' own points,
 * and the goals `wayGoals.ts` turns her to when she stalls in each stage. One
 * set of numbers, so the look cannot point somewhere the prompt is not.
 */
export const BORN_MOTHER_FLANK_AT = { x: CRATE_X + 0.35, y: DECK_Y + 0.2, z: BAY_Z - 0.3 } as const;
export const BORN_STRAW_AT = { x: CRATE_X + 0.55, y: DECK_Y + 0.04, z: CREEP_Z } as const;
/** The taken sibling asleep, at its flank's height: what the `sibling` nudge aims at (D-250). */
export const BORN_SIBLING_AT = { x: SIBLING_TAKEN_AT.x, y: DECK_Y + 0.15, z: SIBLING_TAKEN_AT.z } as const;
/**
 * The walkway door, at the height of a man's chest: what she watches him come
 * through. It was a curtain at x −1.9 hung on a solid wall (beat 1 revision 2,
 * D); the only door in that wall is the walkway's, at x 0.
 */
export const BORN_DOOR_WATCH_AT = { x: 0, y: 0.9, z: -HALL.halfLength - HALL.wallThickness / 2 } as const;

/**
 * Where the man waits when he is not in the room: in the vestibule behind the
 * walkway door, on the doorway's centre line, with the door shut in front of
 * him.
 *
 * Both beats' workers park here (`latchWorkerRoute`). It used to be 2.2 m
 * beyond the end wall at x −1.9 or −0.46: outdoors, so every walk in began by
 * passing through a wall. `walkway-door.spec.ts` walks both routes.
 *
 * x 0: the doorway is 0.9 m between its jambs and he is 0.63 m across at the
 * hands (the `worker` asset, measured by that spec), so the centre is the only
 * line with room either side. Open, the leaf lies along the vestibule's left
 * wall at x −0.42 and he passes it with 0.1 m to spare.
 *
 * z: 0.25 m off the vestibule's back wall, and 1.27 m from the hinge at his
 * nearest corner, outside the leaf's 0.93 m swing.
 */
export const BOOTS_PARK = {
  x: 0,
  z: -HALL.halfLength - HALL.wallThickness - WALKWAY_DOOR.vestibuleDepth + 0.25,
} as const;

/**
 * Where he is through the doorway: on the park's own X, 0.35 m into the hall.
 * Beat 2's round passes it both ways (`latchWorkerRoute`).
 */
export const BOOTS_DOOR_AT = { x: BOOTS_PARK.x, z: -HALL.halfLength + 0.35 } as const;

/**
 * The walkway line both workers walk: 0.36 m in from the pen rail's face.
 *
 * It was 0.24 (x −0.46), and his hands hang 0.317 m out from his line (the
 * `worker` asset), so the hand nearest the pens passed 7 cm into the 1.03 m
 * rail the whole length of the hall. `walkway-door.spec.ts` now checks
 * anything at hand height against his whole footprint.
 */
export const BOOTS_WALKWAY_X = SIDE * (HALL.walkway - 0.36);

/** A place he stands, and the heading he stands at (0 faces +Z). */
export interface WorkerStance {
  x: number;
  z: number;
  facing: number;
}

/** Facing the pens on her side of the walkway. */
const FACING_PENS = SIDE * (Math.PI / 2);

/**
 * How far her pen's gate opens for him, as `penGateLeaf`'s `open`: 2/3, 60°
 * from shut.
 *
 * Not all the way, on purpose (`born-gate.spec.ts`). The leaf swings into
 * the walkway across the whole square in front of the mouth, so while he
 * stands in the mouth the leaf and his body are what close it. At 60° the
 * leaf comes back to within a piglet's width of his hip; laid flat at 90° it
 * leaves a 0.36 m pocket between them, and she would be out of the pen.
 */
export const BORN_GATE_TAKE_OPEN = 2 / 3;

/**
 * Where he lifts the latch and swings the gate: north of the mouth, on the
 * walkway line, outside the leaf's swing.
 *
 * The leaf sweeps the whole square in front of the mouth, so this is the only
 * side of it a man can open it from without the gate going through him. He
 * reaches the latch at the leaf's free end, 0.36 m south and 0.42 m west.
 */
export const BORN_LATCH_STANCE: WorkerStance = { x: BOOTS_WALKWAY_X, z: BAY_Z - 0.86, facing: FACING_PENS };

/**
 * Where he stands to take her littermate: in the open mouth, toes 5 cm short
 * of the shut leaf's line, and bending in through it.
 *
 * His feet never cross onto the pen floor. The gate-access bay behind the
 * mouth is 0.65 × 0.59 m with her mother's head in it, and a man is
 * 0.63 × 0.33 (step 1 of the revision): he does not fit, and she could be
 * standing in it. From here the gate's shut collider, which beat 1 never
 * moves, lies just in front of his boots, so it is his legs she meets.
 *
 * 0.18 m north of the mouth's centre: his 0.634 m across then covers the
 * mouth to within 3 mm of its north end, and the open leaf closes the south.
 */
export const BORN_MOUTH_STANCE: WorkerStance = { x: SIDE * (HALL.walkway - 0.17), z: BAY_Z - 0.18, facing: FACING_PENS };

/**
 * His walk in: out of the vestibule through the doorway, then a quarter turn
 * along the end wall to the latch. Then into the mouth: down the walkway line
 * first and only then across to it, because the diagonal put his near hand
 * through the pen rail north of the mouth (`walkway-door.spec.ts`). The
 * turn is 8 cm short of the mouth's line: turned square on it, his back
 * corner was in the open leaf (`born-gate.spec.ts`).
 *
 * Exported so the route gate (`walkway-door.spec.ts`), the gate's own
 * (`born-gate.spec.ts`) and the plan view check the path `BornDirector`
 * walks rather than a restatement of it. He leaves the same way, reversed.
 */
export const BORN_WALK_IN: readonly { x: number; z: number }[] = [
  { x: BOOTS_PARK.x, z: BOOTS_PARK.z },
  { x: BOOTS_DOOR_AT.x, z: BOOTS_DOOR_AT.z },
  { x: BORN_LATCH_STANCE.x, z: BORN_LATCH_STANCE.z },
];
export const BORN_STEP_IN: readonly { x: number; z: number }[] = [
  { x: BORN_LATCH_STANCE.x, z: BORN_LATCH_STANCE.z },
  { x: BORN_LATCH_STANCE.x, z: BORN_MOUTH_STANCE.z - 0.08 },
  { x: BORN_MOUTH_STANCE.x, z: BORN_MOUTH_STANCE.z },
];

const zone = (() => {
  const found = getZone(FARROWING_ZONE_ID);
  if (!found) throw new Error('born bay: the farrowing zone is missing from the palette');
  return found;
})();

/** A box solid. Mesh and collider both come from this, as everywhere else. */
export interface BaySolid {
  name: string;
  half: { x: number; y: number; z: number };
  pos: { x: number; y: number; z: number };
  /** Solids that only need to stop her, not be seen — the crate rails are meshed
   *  already by the instanced crate, so their colliders are invisible. */
  visible: boolean;
  colour: number;
  kind: 'mineral' | 'metal' | 'organic';
  /** See `HallSolid.role`: the collider `main.ts` keeps a handle to. */
  role?: SolidRole;
}

/** The two pivots that remain live after a littermate's static mesh is merged. */
export interface LittermateParts {
  /** Whole sleeping pose; local X is world up after the authored lie rotation. */
  breath: THREE.Object3D;
  /** One hind leg, kept separate for the rare sleeping twitch. */
  twitch: THREE.Object3D;
  /** Sleeping curl authored by `sleepingPiglet`, restored between twitches. */
  twitchRestX: number;
  /** Resting translation, restored before every breathing correction. */
  breathRestY: number;
  /** Change in the floor point per unit of local-X scale. */
  breathFloorSlope: number;
}

function pigletSolid(
  seed: number,
  at: { x: number; y: number; z: number },
  rotY: number,
  name: string,
): BaySolid {
  // This is deliberately measured from the authored mesh rather than from the
  // footprint declaration. The latter describes a standing preview asset; a
  // folded sleeping animal has a different silhouette, and collision that uses
  // the convenient dimensions would become invisible floor around her.
  const source = sleepingPiglet(seed);
  source.updateMatrixWorld(true);
  const bounds = new THREE.Box3().setFromObject(source);
  const center = bounds.getCenter(new THREE.Vector3());
  const half = bounds.getSize(new THREE.Vector3()).multiplyScalar(0.5);
  // Built purely to be measured, and never added to a scene — so it has to be
  // put back. `sleepingPiglet` is a factory: what it returns owns geometries and
  // materials on the GPU, and dropping the reference frees the JavaScript object
  // and nothing else. Same trap the strip curtain fell into (D-019's neighbour).
  disposeAsset(source);
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);

  return {
    name,
    // A yawed box needs its world-axis footprint, not its unrotated dimensions:
    // |cos| hx + |sin| hz, and the complementary term for Z. Rapier receives an
    // axis-aligned solid, so rotating a smaller cuboid would let her enter fur
    // at the corners of the visible animal.
    half: {
      x: Math.abs(c) * half.x + Math.abs(s) * half.z,
      y: half.y,
      z: Math.abs(s) * half.x + Math.abs(c) * half.z,
    },
    pos: {
      x: at.x + c * center.x + s * center.z,
      y: at.y + center.y,
      z: at.z - s * center.x + c * center.z,
    },
    visible: false,
    colour: 0x000000,
    kind: 'organic',
  };
}

/**
 * Everything solid in her bay.
 *
 * Most of it is invisible, and that is the point: the crate, the slats and the
 * kerb are already drawn by the hall's instanced dressing, so what is missing is
 * only the physics. Meshing them again would double the draw calls for the one
 * bay the player spends three minutes looking at.
 */
export function bornBaySolids(): BaySolid[] {
  const solids: BaySolid[] = [];
  const s = SIDE;

  // The slat deck. She stands on this for the whole beat, and until now it did
  // not exist as collision at all — the pens were unreachable, so nothing had
  // ever needed to hold her up inside one.
  //
  // 1.2 m of half-extent, not 1.55, so the collider matches the two 1.2 m slat
  // tiles the hall actually lays down (`dressFarrowingHall` places them at
  // dx = 0.6 and 1.8 from `penInner`, i.e. covering 0.82 m to 3.22 m out). At
  // 1.55 there was a 0.7 m strip of invisible floor behind the visible deck that
  // she could walk on — and the comment above claimed mesh and collider came
  // from one list, which is exactly the confident-comment failure this repo
  // keeps finding. Found on independent review.
  solids.push({
    name: 'bay-deck',
    half: { x: 1.2, y: SLAT_HEIGHT / 2, z: BAY_PITCH / 2 },
    pos: { x: s * (PEN_INNER + 1.2), y: SLAT_HEIGHT / 2, z: BAY_Z },
    visible: false,
    colour: 0x000000,
    kind: 'mineral',
  });

  // The crate's two lane partitions, as slabs rather than as fifty pipes.
  //
  // A collider per rail would be fifty convex shapes for one crate, and the
  // character controller would have to resolve contacts against all of them
  // every step. One slab per partition is what she actually experiences: a wall
  // she can see through. She is 0.32 m tall and the lowest rail is at 0.18, so
  // there is nothing to crawl under and nothing to lose by simplifying.
  // Offset in Z and running along X, because the crate is laid a quarter turn.
  // See the note above CREEP_Z.
  //
  // The two lane rails now end at the crate's next existing post line, making
  // the gate bay's visible doorway and its collision agree. The outer creep
  // kerbs remain full-length: they still define the pen's outboard boundary.
  // Before the doorway, these runs ended on the kerb rather than 0.10 m short. See
  // KERB_INNER_X: the slot that gap left is the R-023 softlock, and it is closed
  // by arithmetic that names the kerb instead of by a wider half-extent that
  // happens to reach it. The outer end is unchanged and stays tucked under the
  // crate mesh, so nothing visible moves — the 0.10 m strip that disappears was
  // never traversable anyway, being narrower than she is.
  // `+ SIDE * 1.05`, not `- 1.05`: the outer end is 1.05 m further from the
  // walkway, which is −X on this side and +X on the other. Written as a bare
  // subtraction it is correct only for `SIDE = -1` and collapses the run from
  // 2.26 m to 0.16 m on a mirrored bay, silently reopening the escape this file
  // exists to close.
  //
  // Latent rather than live — `SIDE` is a constant and there is one bay — so
  // this is written for the day beat 2 wants the other side. **Nothing tests
  // it**, because nothing can while `SIDE` cannot vary; the arithmetic was
  // checked by substituting both signs by hand, and that is the whole assurance.
  //
  // Found on independent review, which proposed `- SIDE * 1.05`. That is the
  // same bug facing the other way: it collapses the run to 0.16 m on the side
  // we actually ship, which is why the suggested fix was checked instead of
  // applied.
  const outerLaneRun = spanX(CRATE_X + SIDE * 1.05, KERB_SEAL_X);
  // The crate is turned side-to-side in the hall, so its local Z becomes this
  // side's world X. This is the same -0.5 m post line the special mesh uses.
  const gateAccessEndX = farrowingGateAccessRailEnd(CRATE_X, SIDE);
  const laneRun = spanX(CRATE_X + SIDE * 1.05, gateAccessEndX);
  for (const dz of [-LANE_PARTITION_DZ, LANE_PARTITION_DZ]) {
    solids.push({
      name: `lane-partition-${dz < 0 ? 'near' : 'far'}`,
      half: { x: laneRun.halfX, y: 0.5, z: BAY_RAIL_HALF },
      pos: { x: laneRun.centreX, y: DECK_Y + 0.5, z: BAY_Z + dz },
      visible: false,
      colour: 0x000000,
      kind: 'metal',
    });
  }

  // The crate's outer creep kerbs, which pen her in on the far side.
  for (const dz of [-CREEP_KERB_DZ, CREEP_KERB_DZ]) {
    solids.push({
      name: `creep-kerb-${dz < 0 ? 'near' : 'far'}`,
      half: { x: outerLaneRun.halfX, y: 0.13, z: BAY_RAIL_HALF },
      pos: { x: outerLaneRun.centreX, y: DECK_Y + 0.13, z: BAY_Z + dz },
      visible: false,
      colour: 0x000000,
      kind: 'metal',
    });
  }

  // The near end remains a redundant seal where the kerb, end wall and deck
  // meet. There is no partition on that hall-end line, but the end wall is
  // drawn, so it is not an invisible border and removing it would change
  // containment. The far divider used to be the matching loop member; it now
  // lives in `farrowing.ts` as a drawn pen partition on exactly that z line,
  // whose box strictly contains this run (the same X span and y = 0…0.65 rather
  // than this divider's y = 0.09…0.65).
  //
  // This run reaches into the kerb and 0.7 m beyond the deck so the corner and
  // the trench behind it stay sealed rather than merely meeting face to face.
  const dividerRun = spanX(KERB_INNER_X + s * 3.1, KERB_SEAL_X);
  solids.push({
    name: 'bay-divider-near',
    half: { x: dividerRun.halfX, y: 0.28, z: 0.03 },
    pos: { x: dividerRun.centreX, y: DECK_Y + 0.28, z: BAY_Z - BAY_PITCH / 2 },
    visible: false,
    colour: 0x000000,
    kind: 'metal',
  });

  // The sleeping bodies themselves are solids too. They are intentionally not
  // re-meshed: their visible meshes are already in `createBornBay`, and adding
  // placeholder boxes here would spend draw calls on collision we cannot see.
  solids.push(pigletSolid(1, SIBLING_KEPT_AT, -0.4, 'sibling-kept'));
  solids.push({ ...pigletSolid(2, SIBLING_TAKEN_AT, SIBLING_TAKEN_YAW, 'sibling-taken'), role: 'taken-sibling' });
  solids.push(...sowSolids());

  return solids;
}

/**
 * Her mother, as two boxes: the body, and the head that reaches into the
 * gate-access bay.
 *
 * Until revision 2 she had no collider at all, and Rosalie walked through her
 * head (Alex, 2026-10-01). Nothing caught it because every gate here asked
 * whether Rosalie could get OUT of the pen, and the head is inside it. The
 * gate that now catches it is `born-regions.spec.ts`: it floods the bay from
 * the spawn and fails if she reaches anywhere `bornAllowedRegions` does not
 * name — which, without these two boxes, is the whole length of the sow's lane.
 *
 * Two boxes and not one, because one box round a lying sow is mostly air: the
 * body spans 0.69 m across the lane and the head 0.49 m, so a single box
 * would close the gate-access bay beside her face, where Rosalie can visibly
 * stand. Measured from the placed mesh, as `pigletSolid` does, for the same
 * reason: the footprint declaration describes a standing animal.
 *
 * The head box is the union of the resting and the fully lifted pose. Both
 * tops are 0.58 m, 0.49 m above the deck and beyond the 0.34 m scramble limit,
 * so neither box is somewhere she can climb (`bay-staging.spec.ts`).
 */
function sowSolids(): BaySolid[] {
  const rest = sowBounds(0);
  const lifted = sowBounds(SOW_HEAD_LIFT_MAX);
  const solid = (name: string, box: THREE.Box3): BaySolid => {
    const centre = box.getCenter(new THREE.Vector3());
    const half = box.getSize(new THREE.Vector3()).multiplyScalar(0.5);
    return {
      name,
      half: { x: half.x, y: half.y, z: half.z },
      pos: { x: centre.x, y: centre.y, z: centre.z },
      visible: false,
      colour: 0x000000,
      kind: 'organic',
    };
  };
  return [solid('sow-body', rest.body), solid('sow-head', rest.head.union(lifted.head))];
}

/** A rectangle in plan, world metres. */
export interface BayRegion {
  name: string;
  xMin: number;
  xMax: number;
  zMin: number;
  zMax: number;
}

/**
 * Where her capsule centre may be: inside some `regions` rectangle and at least
 * her radius from every `keepOut` footprint. Rectangles alone cannot say it,
 * because a capsule goes round a box's corner on a quarter circle.
 */
export interface BayArea {
  regions: BayRegion[];
  keepOut: BayRegion[];
}

/**
 * Where Rosalie is meant to be able to stand in beat 1, written down as data
 * (`docs/beat-1-revision-plan.md`, A). Before this the answer was "whatever
 * the solids leave", and what they left included the inside of her mother.
 *
 * Regions are in terms of her capsule centre, so every edge is a boundary line
 * pulled in by her radius. The lines are the bay's own: the creep kerbs, the
 * lane partitions' outer faces, the gate-access post line, the hall kerb, the
 * shut gate, the back of the crate.
 *
 * - **The near creep**, where she is born: the lamp, the straw, the siblings.
 * - **The far creep**, along the sow's other side. Real crates have one (the
 *   litter feeds from both rows of teats). Nothing in beat 1 needs it, but it
 *   is pen floor and she can get there, so it is named rather than left to be
 *   found.
 * - **The gate-access bay** between the post line and the hall kerb, minus her
 *   mother's head (the keep-out). What the head leaves is a 0.32 m strip at the
 *   snout, which her 0.25 m body fits with 0.07 m to spare: the one way between
 *   the two creeps, and where she meets her mother face to face. The strip
 *   beside the head on the far side is 0.23 m, and she does not fit.
 * - **The gate mouth**: the hall kerb stops for the gate, and the shut leaf
 *   stands 0.06 m further out than the kerb's face, so along the mouth she can
 *   press that much closer to the walkway.
 *
 * The head keep-out is the head collider itself, so this says nothing
 * independent about the head. What `born-regions.spec.ts` checks is that
 * nothing ELSE lets her through, which is exactly what was wrong: with no sow
 * collider she reached the whole length of the sow's lane. Heights are not here
 * at all; see `tests/plan-reach.ts`.
 */
export function bornAllowedArea(): BayArea {
  const r = ROSALIE_TUNING.radius;
  const head = sowSolids().find((solid) => solid.name === 'sow-head');
  if (head === undefined) throw new Error('bornAllowedArea: the sow has no head collider');
  // Written for SIDE = -1 (walkway at larger X), which is the side the bay is
  // on. A mirrored bay would need these mirrored too; asserted, so that day
  // fails here rather than silently.
  if (SIDE !== -1) throw new Error('bornAllowedArea: written for the bay on the -X side');
  const gate = soleSolidWithRole(farrowingSolids(), 'shut-pen-gate', 'farrowingSolids()');
  const mouth = penGateMouths().find((m) => Math.abs(m.z - BAY_Z) < 1e-6);
  if (mouth === undefined) throw new Error('bornAllowedArea: no pen gate mouth at the bay');
  const kerb = KERB_INNER_X - r;
  const back = CRATE_X - 1.05 + r;
  const nearKerb = BAY_Z - CREEP_KERB_DZ + BAY_RAIL_HALF + r;
  const farKerb = BAY_Z + CREEP_KERB_DZ - BAY_RAIL_HALF - r;
  const nearLane = BAY_Z - LANE_PARTITION_DZ - BAY_RAIL_HALF - r;
  const farLane = BAY_Z + LANE_PARTITION_DZ + BAY_RAIL_HALF + r;
  return {
    regions: [
      { name: 'near creep', xMin: back, xMax: kerb, zMin: nearKerb, zMax: nearLane },
      { name: 'far creep', xMin: back, xMax: kerb, zMin: farLane, zMax: farKerb },
      { name: 'gate-access bay', xMin: farrowingGateAccessRailEnd(CRATE_X, SIDE), xMax: kerb, zMin: nearLane, zMax: farLane },
      {
        name: 'gate mouth',
        xMin: kerb,
        xMax: gate.pos.x - gate.half.x - r,
        zMin: mouth.z - mouth.width / 2,
        zMax: mouth.z + mouth.width / 2,
      },
    ],
    keepOut: [{
      name: 'sow head',
      xMin: head.pos.x - head.half.x,
      xMax: head.pos.x + head.half.x,
      zMin: head.pos.z - head.half.z,
      zMax: head.pos.z + head.half.z,
    }],
  };
}

/**
 * World bounds of the placed sow, split at the head pivot. Built to be
 * measured and then disposed: `sowAsset.create` owns GPU resources (see
 * `pigletSolid`).
 */
function sowBounds(headLift: number): { body: THREE.Box3; head: THREE.Box3 } {
  const group = sowAsset.create();
  group.position.set(SOW_AT.x, SOW_AT.y, SOW_AT.z);
  group.rotation.y = SOW_FACING;
  const { head } = findSowParts(group);
  head.rotation.x = headLift;
  group.updateMatrixWorld(true);
  const headBox = new THREE.Box3().setFromObject(head);
  const body = new THREE.Box3();
  // Per mesh, from its own geometry: `Box3.setFromObject` on a body node
  // would include the head, which hangs beneath it in the hierarchy.
  const meshBox = new THREE.Box3();
  group.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    for (let up: THREE.Object3D | null = object; up !== null; up = up.parent) if (up === head) return;
    const geometry = object.geometry as THREE.BufferGeometry;
    if (geometry.boundingBox === null) geometry.computeBoundingBox();
    if (geometry.boundingBox === null) return;
    body.union(meshBox.copy(geometry.boundingBox).applyMatrix4(object.matrixWorld));
  });
  disposeAsset(group);
  return { body, head: headBox };
}

/** The heat lamp's light. Its own colour, and the only warm one in the zone. */
export function bornBayFixtures(): Fixture[] {
  return [
    {
      kind: 'point',
      // Below the fitting, not level with it, for the reason `farrowing.ts`
      // gives about the battens: these point lights cast no shadow, so a fitting
      // cannot occlude its own light and the emitter has to be moved instead.
      position: { x: LAMP_AT.x, y: LAMP_AT.y - 0.16, z: LAMP_AT.z },
      // Infrared brooder red. It is the ONLY colour in the farrowing zone that
      // is not on the sickly white-green / concrete-grey axis spec §2 gives it,
      // and it is allowed to exist because it is the one thing in this building
      // built for the animals' comfort rather than the workers'. That irony is
      // the beat, and the palette's 0.2 chroma ceiling keeps it from becoming a
      // theme-park glow.
      colour: 0xd8734a,
      intensity: 3.4,
      range: 2.6,
    },
  ];
}

/** What the bay adds to the hall's ambience: her mother, breathing, up close. */
export function bornBayAmbience(): { id: string; at: { x: number; y: number; z: number }; gain: number }[] {
  return [];
}

/** The handles the beat needs to change things. Nothing else may be touched. */
export interface BornBay {
  group: THREE.Group;
  sow: SowParts;
  /**
   * The ceiling battens close enough to the bay to light it.
   *
   * His bend into the pen dims these, because a body over the pen really
   * does cut the room light as well as the lamp, and because these — not the
   * heat lamp — are why the creep was never dark.
   */
  nearLights: THREE.PointLight[];
  /** The sibling who remains, with only breath and one sleeping twitch live. */
  keptSibling: LittermateParts;
  /** The littermate that is taken. Removing it from the scene IS the beat. */
  takenSibling: THREE.Object3D;
  /** The taken sibling's live pivots, retained until the cue removes its root. */
  takenSiblingParts: LittermateParts;
  /**
   * The same littermate awake (D-250): Rosalie's rigged mesh in the sibling's
   * dye, hidden until it is woken. `root` carries position and yaw, `roll`
   * turns it onto its side and back, `plush` is what `RosalieRig` drives.
   */
  awakeSibling: { root: THREE.Group; roll: THREE.Group; plush: THREE.Group };
  /**
   * The man, parked outside the room until the beat walks him in.
   *
   * Still called `boots` because that is what the beat calls him — the cues are
   * `bootsEnter`, `bootsArrive`, `bootsLeave` and the sound is a footfall. He is
   * a whole `worker` since D-092; the story's name for him has not changed.
   */
  boots: THREE.Object3D;
  /** The lamp's light, so the worker leaning over the pen can block it. */
  lamp: THREE.PointLight | null;
}

/** One littermate, merged except for the two pivots the sleeping performance needs. */
function livePiglet(
  seed: number,
  at: { x: number; y: number; z: number },
  rotY: number,
  name: string,
): { root: THREE.Group; parts: LittermateParts } {
  const source = sleepingPiglet(seed);
  source.name = name;
  source.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh) {
      m.castShadow = true;
      m.receiveShadow = true;
    }
  });

  const lie = source.getObjectByName('piglet-lie');
  if (!lie) throw new Error('born bay: piglet-lie is missing from sleepingPiglet');
  const twitch = source.getObjectByName('leg.BL');
  if (!twitch) throw new Error('born bay: piglet hind leg is missing from sleepingPiglet');
  const twitchParent = twitch.parent;

  // Detach BEFORE collapsing, then restore to precisely the parent it came from.
  // `collapse` merges every mesh below `lie`; leaving this leg below it would
  // bake the twitch away. More importantly, reattaching it to a detached holder
  // reproduces the sow-head bug documented above: the pivot exists, but not in
  // the scene. The original parent survives under `lie`, so it is the only safe
  // place to put it back.
  twitch.removeFromParent();
  collapse(lie, `${name}-body`);
  twitchParent?.add(twitch);

  // Local X becomes world Y under lie.rotation.z = pi / 2. Measure the floor
  // response from the real mesh at 1x and 1.025x so render-time breathing can
  // compensate its low point without a Box3 allocation every frame.
  source.updateMatrixWorld(true);
  const restFloor = new THREE.Box3().setFromObject(source).min.y;
  lie.scale.x = 1.025;
  source.updateMatrixWorld(true);
  const scaledFloor = new THREE.Box3().setFromObject(source).min.y;
  lie.scale.x = 1;
  // `collapse` bakes child meshes relative to its live pivot. Place the root
  // only after that bake: doing it first bakes this world placement and then
  // applies it again through the parent transform, the same kind of stacked-axis
  // error the bay's first staging pass taught us to distrust.
  // The detached leg is still part of the measured resting silhouette. Its
  // reattachment can make a different extremity define the merged remainder's
  // floor, so retain the measured contact plane rather than assuming zero.
  source.position.set(at.x, at.y - restFloor, at.z);
  source.rotation.y = rotY;

  return {
    root: source,
    parts: {
      breath: lie,
      twitch,
      twitchRestX: twitch.rotation.x,
      breathRestY: lie.position.y,
      breathFloorSlope: (scaledFloor - restFloor) / 0.025,
    },
  };
}

function assetOrThrow(id: string): THREE.Group {
  const asset = getAsset(id);
  if (!asset) throw new Error(`born bay: asset "${id}" is not registered`);
  return asset.create();
}

/**
 * Build the bay's contents, on top of the hall that already exists.
 *
 * `lamp` is looked up out of the zone rig rather than created here, because
 * `docs/asset-conventions.md` forbids an asset factory from making a light and
 * the same reasoning applies one level up: the room owns its lighting, and a
 * second place that creates lights is a second place that can disagree with the
 * exposure reference.
 */
export function createBornBay(
  lampLight: THREE.PointLight | null,
  nearLights: readonly THREE.PointLight[] = [],
): BornBay {
  const group = new THREE.Group();
  group.name = 'born-bay';

  // ── her mother ─────────────────────────────────────────────────────────────
  const sowGroup = sowAsset.create();
  sowGroup.position.set(SOW_AT.x, SOW_AT.y, SOW_AT.z);
  sowGroup.rotation.y = SOW_FACING;
  sowGroup.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh) {
      m.castShadow = true;
      m.receiveShadow = true;
    }
  });

  // Collapse her, but keep the two nodes that move.
  //
  // The head is detached first so that collapsing her body cannot bake its
  // transform into a merged geometry, and it is re-attached to the same pivot it
  // came off — which only works because `collapse` leaves the pivot hierarchy
  // standing. See the note on `collapse`; the version that did not cost this
  // sow her head.
  const sowParts = findSowParts(sowGroup);
  const headParent = sowParts.head.parent;
  sowParts.head.removeFromParent();
  collapse(sowParts.breath, 'sow-body');
  collapse(sowParts.head, 'sow-head');
  headParent?.add(sowParts.head);

  group.add(sowGroup);

  // ── the straw ──────────────────────────────────────────────────────────────
  // Scattered over the creep area, seeded so the bed is identical on every run.
  // A bed that reshuffles between runs makes every screenshot in
  // docs/verification/ unreproducible, which is most of what that folder is for.
  const straw = new THREE.Group();
  straw.name = 'creep-straw';
  // Along the creep's long axis, which is X. Two rows, because the strip is
  // 0.54 m deep and one row down the middle reads as a path rather than a bed.
  const strawSpots: [number, number][] = [
    [-0.85, -0.11], [-0.5, 0.1], [-0.12, -0.09], [0.22, 0.12],
    [0.55, -0.12], [0.88, 0.08], [-0.2, 0.16], [0.2, -0.16],
  ];
  for (const [i, spot] of strawSpots.entries()) {
    const clump = strawClump(i * 13, zone.straw ?? 0xbcaa79);
    clump.position.set(CRATE_X + spot[0], DECK_Y, CREEP_Z + spot[1]);
    clump.rotation.y = i * 0.9;
    straw.add(clump);
  }
  // Merged by surface, at a single placement.
  //
  // Eight clumps of three meshes each is 24 draw calls for a bed of straw, and
  // it pushed the dressed hall from 400 to 449 against spec §1's budget — the
  // first time in this project that budget has actually bitten. `instanceGroup`
  // with one placement is the merge without the instancing, which is exactly
  // what static scenery wants: the clumps have different geometry (different
  // seeds) so they cannot be instanced, but they share three materials so they
  // can be merged. 24 calls become 3.
  group.add(instanceGroup(straw, [{ x: 0, z: 0 }], 'creep-straw'));

  // ── her littermates ────────────────────────────────────────────────────────
  // Each littermate is merged into its own group rather than both into one.
  //
  // Rosalie's mesh is about twenty named pivots, so two of them unmerged is
  // forty draw calls for two sleeping props. Merging them TOGETHER would be
  // cheaper still and is wrong: the beat removes one of them from the scene, and
  // a shared merged geometry cannot have half of itself taken away. This is the
  // one place in the room where the cheaper thing is not the right thing.
  const kept = livePiglet(1, SIBLING_KEPT_AT, -0.4, 'sibling-kept');
  group.add(kept.root);

  const taken = livePiglet(2, SIBLING_TAKEN_AT, SIBLING_TAKEN_YAW, 'sibling-taken');
  group.add(taken.root);

  // The same littermate, awake: hidden (not drawn) until it is woken, and the
  // sleeper is hidden in the same frame.
  //
  // Merged down to one mesh per material per rig pivot, because its legs walk
  // and its head turns but nothing else on it moves alone. Built raw it is 19
  // meshes, every one a shadow caster, and waking it measured +52 draw calls —
  // 315 → 367 against the hall's 336 ceiling (`sibling-frames.spec`, the wake
  // delta). The pivots are exactly what `findRosalieParts` resolves, so the
  // rig still finds and moves all of them. That alone left +24 (339), so the
  // snout, ears and tail cast no shadow either: under the hall's high key they
  // are a few centimetres of shadow inside the body's own, and each one is a
  // second draw in the shadow pass.
  const awakePlush = buildPlush(SIBLING, 'sibling-awake-body');
  const awakeParts = findRosalieParts(awakePlush);
  collapseRigged(awakePlush, Object.values(awakeParts));
  const pivots = new Set<THREE.Object3D>(Object.values(awakeParts));
  const noShadow = new Set<THREE.Object3D>([awakeParts.snout, awakeParts.earL, awakeParts.earR, awakeParts.tail]);
  awakePlush.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    // `collapse` puts the merged mesh in a group under its pivot, so the
    // nearest pivot ancestor, not the parent, says which part it is.
    let owner = m.parent;
    while (owner !== null && !pivots.has(owner)) owner = owner.parent;
    m.castShadow = owner === null || !noShadow.has(owner);
    m.receiveShadow = true;
  });
  const awakeRoll = new THREE.Group();
  awakeRoll.name = 'sibling-awake-roll';
  awakeRoll.add(awakePlush);
  awakePlush.position.y = -AWAKE_SIBLING_CENTRE_Y;
  awakeRoll.position.y = AWAKE_SIBLING_CENTRE_Y;
  const awakeRoot = new THREE.Group();
  awakeRoot.name = 'sibling-awake';
  awakeRoot.add(awakeRoll);
  awakeRoot.position.set(SIBLING_TAKEN_AT.x, DECK_Y, SIBLING_TAKEN_AT.z);
  awakeRoot.rotation.y = SIBLING_TAKEN_YAW;
  awakeRoot.visible = false;
  group.add(awakeRoot);

  // ── the heat lamp ──────────────────────────────────────────────────────────
  // Turned over, exactly as the ceiling battens are: the fitting is authored
  // from its mounting face upward, so hanging it is a rotation about X.
  const lamp = assetOrThrow('heat-lamp');
  lamp.position.set(LAMP_AT.x, LAMP_AT.y, LAMP_AT.z);
  lamp.rotation.x = Math.PI;
  lamp.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh) m.castShadow = true;
  });
  collapse(lamp, 'heat-lamp-merged');
  group.add(lamp);

  // ── the man ────────────────────────────────────────────────────────────────
  // Parked in the vestibule behind the shut walkway door (`BOOTS_PARK`) rather
  // than hidden with `visible = false`.
  //
  // A hidden object still exists to `Box3`, to a NaN sweep and to anything that
  // traverses the scene, and the harness's transform check walks everything. It
  // also means the walk-in is one interpolation from a real position rather than
  // a pop from nowhere on the frame visibility flips.
  //
  // **A whole figure, and it used to be a cropped pair of legs.** D-088 argued
  // that boots under a drawn curtain at a newborn's eye level was a composed
  // shot; playing it says otherwise (D-092). The crop was authored for a camera
  // that never happens — the beat hands the camera to the player at 1.1 m and
  // she can walk to the kerb and look up the hall — so what a player actually
  // sees is a man with no body. He is not `collapse`d: his hips and shoulders
  // are rig pivots and `BornDirector` swings them, so a merged subtree here
  // would be a figure that slides down the hall with its legs held still, which
  // is the failure D-088 named and then only fixed in the other two beats.
  const boots = assetOrThrow('worker');
  boots.name = 'worker-boots';
  boots.position.set(BOOTS_PARK.x, 0, BOOTS_PARK.z);
  boots.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh) m.castShadow = true;
  });
  group.add(boots);

  return {
    group,
    sow: sowParts,
    keptSibling: kept.parts,
    takenSibling: taken.root,
    takenSiblingParts: taken.parts,
    awakeSibling: { root: awakeRoot, roll: awakeRoll, plush: awakePlush },
    boots,
    nearLights: [...nearLights],
    lamp: lampLight,
  };
}

/**
 * The dark line under the crate, which the hall does not draw.
 *
 * Purely a value: from her height the slat deck and the crate's feet meet with
 * nothing between them, and the eye needs a dark to sit the whole assembly on or
 * the crate reads as floating over a grey field. One box, no collider.
 */
export function bayShadowLine(): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.BoxGeometry(2.1, 0.004, BAY_PITCH * 0.92),
    surface('mineral', zone.surfaces[0] ?? 0x39433f),
  );
  mesh.name = 'bay-shadow-line';
  mesh.position.set(CRATE_X, DECK_Y + 0.003, BAY_Z);
  mesh.receiveShadow = true;
  return mesh;
}
